<script lang="ts">
	import ChromoButton from "@components/ChromoButton.svelte";
	import PageHero from "@components/PageHero.svelte";
	import ChromosomeSelector from "@components/chromosomes/ChromosomeSelector.svelte";
	import Button from "@components/common/Button.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import { onMount } from "svelte";
	import { Chromosome } from "@core/schema/Chromosome.js";
	import { getChromosomeSearchManufacturerPath } from "@core/schema/paths/getChromosomeSearchManufacturerPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import type { ChromosomeSearchProps } from "./ChromosomeSearchProps.js";

	let {
		selectedManufacturer,
		/* eslint-disable prefer-const */
		manufacturers,
		products,
		/* eslint-enable prefer-const */
	}: ChromosomeSearchProps = $props();
	const { tenant } = getGlobalTemplateProps();
	const manufacturerProducts = $derived(
		products.filter(({ manufacturer }) => manufacturer === selectedManufacturer?.id),
	);
	let selectedChromosome = $state<Chromosome | null>(null);

	onMount(() => {
		const search = new URLSearchParams(window.location.search);
		const chromosome = search.get("chromosome");
		if (chromosome !== null) {
			selectedChromosome = chromosome as Chromosome;
		}
	});

	$effect(() => {
		const url = new URL(window.location.toString());
		if (selectedChromosome !== null) {
			url.searchParams.set("chromosome", selectedChromosome);
		} else {
			url.searchParams.delete("chromosome");
		}
		window.history.replaceState(null, "", url.toString());
	});

	const filteredManufacturers = $derived.by(() => {
		if (selectedChromosome === null) {
			return manufacturers;
		}

		return manufacturers.filter((manufacturer) => {
			if (manufacturer.id === selectedManufacturer?.id) {
				return true;
			}
			const manufacturerProducts = products.filter((product) => product.manufacturer === manufacturer.id);
			return manufacturerProducts.some(({ locations }) =>
				locations.some((chromosome) => chromosome.chromosome === selectedChromosome),
			);
		});
	});
</script>

<Layout>
	<PageHero
		headline="Vyhledejte FISH sondu podle chromozomu"
		description="Každá sonda je navržena pro detekci specifických chromozomálních změn. Díky vyhledávači snadno najdete přesně tu, kterou potřebujete."
		highlightedDescription="Vyberte správnou FISH sondu pro přesnou diagnostiku a výzkum."
	/>

	<section class="grid-container full-width pt-20 pb-32 bg-gradient-to-t from-light/50 to-light">
		<div class="mx-auto">
			<h2 class="text-center"><Formatted text="Vyberte chromozom" /></h2>
			<div class="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 pt-12 pb-10 gap-3">
				{#each Object.values(Chromosome) as chromosome (chromosome)}
					<label>
						<input
							type="radio"
							name="chromosome"
							class="opacity-0 pointer-events-none absolute inset-0"
							value={chromosome}
							bind:group={selectedChromosome}
						/>
						<ChromoButton {chromosome} active={selectedChromosome === chromosome} />
					</label>
				{/each}
			</div>
			<div class="flex flex-col md:flex-row items-center gap-8 justify-center">
				<span class="text-lightblue font-semibold">Vyberte výrobce</span>
				<div class="flex items-center gap-2 flex-wrap">
					{#each filteredManufacturers as manufacturer (manufacturer.id)}
						<Button
							onclick={(): void => {
								window.history.replaceState(
									null,
									"",
									getChromosomeSearchManufacturerPath(tenant, manufacturer, selectedChromosome),
								);
								selectedManufacturer = manufacturer;
							}}
							variant={selectedManufacturer?.id === manufacturer.id ? ButtonVariant.PillActive : ButtonVariant.Pill}
						>
							<Formatted text={manufacturer.name[tenant.locale]} />
						</Button>
					{/each}
				</div>
			</div>

			{#if selectedChromosome !== null && selectedManufacturer !== null}
				<div class="mt-16">
					<ChromosomeSelector chromosome={selectedChromosome} products={manufacturerProducts} />
				</div>
			{/if}
		</div>
	</section>
</Layout>
